import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BackupCodesPanel = ({ codes, onContinue }) => {
  const [downloaded, setDownloaded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownload = () => {
    const content = `TrainaryGym 2FA Backup Codes\n\nGenerated: ${new Date()?.toLocaleString()}\n\nIMPORTANT: Keep these codes in a safe place. Each code can only be used once.\n\n${codes?.map((code, i) => `${i + 1}. ${code}`)?.join('\n')}\n\nIf you lose access to your authenticator app, you can use these codes to log in.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trainarygym-backup-codes-${Date.now()}.txt`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
    
    setDownloaded(true);
  };

  const handleCopy = () => {
    const text = codes?.join('\n');
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow?.document?.write(`
      <html>
        <head>
          <title>TrainaryGym Backup Codes</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #333; }
            .warning { background: #fff3cd; padding: 15px; border-left: 4px solid #ffc107; margin: 20px 0; }
            .codes { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin: 20px 0; }
            .code { padding: 10px; background: #f5f5f5; font-family: monospace; font-size: 16px; }
          </style>
        </head>
        <body>
          <h1>TrainaryGym 2FA Backup Codes</h1>
          <p><strong>Generated:</strong> ${new Date()?.toLocaleString()}</p>
          <div class="warning">
            <strong>IMPORTANT:</strong> Keep these codes in a safe place. Each code can only be used once.
          </div>
          <div class="codes">
            ${codes?.map((code, i) => `<div class="code">${i + 1}. ${code}</div>`)?.join('')}
          </div>
          <p><small>If you lose access to your authenticator app, you can use these codes to log in.</small></p>
        </body>
      </html>
    `);
    printWindow?.document?.close();
    printWindow?.print();
  };

  return (
    <div className="card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
          <Icon name="Key" size={24} className="text-warning" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Save Your Backup Codes</h2>
          <p className="text-sm text-muted-foreground mt-1">Store these codes securely for account recovery</p>
        </div>
      </div>
      {/* Warning */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">Important Security Information</p>
            <ul className="text-muted-foreground space-y-1">
              <li>• Each code can only be used once</li>
              <li>• Store these codes in a secure location</li>
              <li>• Don't share these codes with anyone</li>
              <li>• You can generate new codes anytime from your settings</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Backup Codes Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {codes?.map((code, index) => (
          <div
            key={index}
            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border"
          >
            <span className="text-xs text-muted-foreground font-medium w-6">{index + 1}.</span>
            <code className="flex-1 text-sm font-mono text-foreground">{code}</code>
          </div>
        ))}
      </div>
      {/* Action Buttons */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Button
          variant="outline"
          onClick={handleDownload}
          className="flex items-center justify-center gap-2"
        >
          <Icon name="Download" size={16} />
          Download
        </Button>
        <Button
          variant="outline"
          onClick={handleCopy}
          className="flex items-center justify-center gap-2"
        >
          <Icon name={copied ? "Check" : "Copy"} size={16} />
          {copied ? 'Copied!' : 'Copy'}
        </Button>
        <Button
          variant="outline"
          onClick={handlePrint}
          className="flex items-center justify-center gap-2"
        >
          <Icon name="Printer" size={16} />
          Print
        </Button>
      </div>
      {/* Storage Recommendations */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-2">Recommended Storage Options:</p>
            <ul className="text-muted-foreground space-y-1">
              <li>• Password manager (1Password, LastPass, Bitwarden)</li>
              <li>• Encrypted cloud storage</li>
              <li>• Physical safe or secure location</li>
              <li>• Printed copy in a locked drawer</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Confirmation Checkbox */}
      <div className="bg-muted/30 rounded-lg p-4 mb-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={downloaded}
            onChange={(e) => setDownloaded(e?.target?.checked)}
            className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm text-foreground">
            I have saved my backup codes in a secure location and understand that I won't be able to see them again.
          </span>
        </label>
      </div>
      {/* Continue Button */}
      <Button
        onClick={onContinue}
        disabled={!downloaded}
        className="w-full"
      >
        Continue to Complete Setup
      </Button>
    </div>
  );
};

export default BackupCodesPanel;